import { Controller } from "@hotwired/stimulus"
import $ from "jquery"
import "slick-carousel"

const POST_VISIBILITY_THRESHOLD = 0.7
const SCROLL_ROOT_SELECTOR = ".layout-responsive"

function resolveScrollRoot(element) {
  const candidate = element.closest(SCROLL_ROOT_SELECTOR)
  if (!candidate) return null

  const overflowY = globalThis.getComputedStyle(candidate).overflowY
  return overflowY === "auto" || overflowY === "scroll"
    ? candidate
    : null
}

export default class extends Controller {
  static targets = ["slider"]

  connect() {
    this.element.feedVideoController = this

    this.scrollRoot = resolveScrollRoot(this.element)
    this.isPostVisible = false

    this.handleTabVisibility = this.handleTabVisibility.bind(this)
    this.handleTurboBeforeCache = this.handleTurboBeforeCache.bind(this)
    this.handleTurboFrameLoad = this.handleTurboFrameLoad.bind(this)

    document.addEventListener(
      "visibilitychange",
      this.handleTabVisibility
    )

    document.addEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    )

    document.addEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad
    )

    this.setupPostObserver()
    this.initializeSlider()
  }

  disconnect() {
    clearTimeout(this.readyRetryTimeout)
    this.layoutObserver?.disconnect()

    document.removeEventListener(
      "visibilitychange",
      this.handleTabVisibility
    )

    document.removeEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    )

    this.destroySlider()

    this.postObserver?.disconnect()

    this.pauseAll()

    this.element.feedVideoController = null
  }

  handleTurboBeforeCache() {
    clearTimeout(this.readyRetryTimeout)
    this.pauseAll()
  }

  initializeSlider() {
    if (!this.hasSliderTarget) return

    const $slider = $(this.sliderTarget)

    if ($slider.hasClass("slick-initialized")) {
      // Turbo caches a cloned DOM tree, so Slick's generated markup survives
      // but its JavaScript instance does not. Keep the live slider intact
      // during navigation, then rebuild only a disconnected cached clone.
      if (this.sliderTarget.slick) return

      this.resetCachedSliderMarkup($slider)
    }

    // A single media item has nothing to slide. Keeping it out of Slick avoids
    // any first-paint-to-carousel layout change while retaining video control.
    if (this.sliderTarget.classList.contains("post-media-slider--single")) {
      this.markSliderReady()
      return
    }

    // Slick calculates slide widths from the rendered container. Turbo frames
    // and Bootstrap modals can connect while that container is still hidden,
    // which gives Slick a zero width and causes a visible second layout pass.
    if (this.sliderTarget.getBoundingClientRect().width === 0) {
      this.observeSliderLayout()
      return
    }

    this.layoutObserver?.disconnect()
    this.layoutObserver = null

    // A cached Turbo snapshot must not leave generated navigation behind.
    $slider.children(".slick-dots").remove()

    $slider.on("init.feedVideo reInit.feedVideo", (event, slick) => {
      this.markSliderReady()
      this.syncActiveDot(slick)

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide()
        }
      })
    })

    $slider.on("beforeChange.feedVideo", () => {
      this.pauseAll()
    })

    $slider.on("afterChange.feedVideo", (event, slick) => {
      this.syncActiveDot(slick)

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide()
        }
      })
    })

    $slider.on("breakpoint.feedVideo setPosition.feedVideo", (event, slick) => {
      this.syncActiveDot(slick)

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide()
        }
      })
    })

    const isInModal = this.element.closest(".modal") !== null
    const initialSlide = Number.parseInt(
      this.sliderTarget.dataset.initialSlide || "0",
      10
    )

    $slider.slick({
      initialSlide,
      infinite: false,
      slidesToShow: isInModal ? 2 : 3,
      slidesToScroll: 1,
      arrows: false,
      dots: true,
      draggable: true,
      swipe: true,
      touchMove: true,
      adaptiveHeight: false,

      responsive: [
        {
          breakpoint: 1870,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 1500,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 1400,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 900,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 700,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        },
        {
          breakpoint: 690,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: false,
            dots: true
          }
        }
      ]
    })
  }

  observeSliderLayout() {
    if (this.layoutObserver) return

    this.layoutObserver = new globalThis.ResizeObserver(entries => {
      const hasWidth = entries.some(entry => entry.contentRect.width > 0)
      if (!hasWidth) return

      this.layoutObserver.disconnect()
      this.layoutObserver = null
      this.initializeSlider()
    })

    this.layoutObserver.observe(this.sliderTarget)
  }

  markSliderReady() {
    this.sliderTarget.classList.add("post-media-slider--ready")
    delete this.sliderTarget.dataset.initialSlide
  }

  resetCachedSliderMarkup($slider) {
    const $track = $slider.children(".slick-list").children(".slick-track")
    const $slickSlides = $track.children(".slick-slide:not(.slick-cloned)")
    const currentIndex = Number.parseInt(
      $slickSlides.filter(".slick-current").attr("data-slick-index") || "0",
      10
    )

    // Slick's default row wrapper is: slick-slide > row > original slide.
    // Reuse those original nodes so nested video/fancybox markup stays intact.
    let $originalSlides = $slickSlides.children().children()
    if ($originalSlides.length === 0) $originalSlides = $slickSlides

    $originalSlides.detach().removeAttr("style")
    $slider.empty().append($originalSlides)
    $slider
      .removeClass(
        "slick-slider slick-initialized slick-dotted post-media-slider--ready"
      )
      .removeAttr("style")

    this.sliderTarget.dataset.initialSlide = currentIndex
  }

  syncActiveDot(slick) {
    if (!slick?.$dots) return

    const scrollSize = Math.max(1, slick.options.slidesToScroll)
    const dotIndex = Math.min(
      Math.floor(slick.currentSlide / scrollSize),
      slick.$dots.children().length - 1
    )

    slick.$dots.children().removeClass("slick-active")
    slick.$dots.children().eq(dotIndex).addClass("slick-active")
  }

  destroySlider() {
    if (!this.hasSliderTarget) return

    this.layoutObserver?.disconnect()
    this.layoutObserver = null

    const $slider = $(this.sliderTarget)

    if ($slider.hasClass("slick-initialized")) {
      $slider.off(".feedVideo")

      $slider.slick("unslick")
    }

    this.sliderTarget.classList.remove("post-media-slider--ready")
  }

  setupPostObserver() {
    this.postObserver = new IntersectionObserver(
      this.handlePostIntersection.bind(this),
      {
        root: this.scrollRoot,
        threshold: POST_VISIBILITY_THRESHOLD
      }
    )

    this.postObserver.observe(this.element)

    this.isPostVisible = this.isInViewportNow()

    if (this.isPostVisible) {
      requestAnimationFrame(() => this.playCurrentSlide())
    }
  }

  isInViewportNow() {
    const rect = this.element.getBoundingClientRect()

    if (rect.height === 0) return false

    const rootRect = this.scrollRoot
      ? this.scrollRoot.getBoundingClientRect()
      : {
        top: 0,
        bottom: globalThis.innerHeight
      }

    const visibleHeight =
      Math.min(rect.bottom, rootRect.bottom) -
      Math.max(rect.top, rootRect.top)

    return (
      Math.max(0, visibleHeight) / rect.height >=
      POST_VISIBILITY_THRESHOLD
    )
  }

  handlePostIntersection(entries) {
    entries.forEach(entry => {
      this.isPostVisible = entry.isIntersecting

      if (entry.isIntersecting) {
        requestAnimationFrame(() => {
          this.playCurrentSlide()
        })
      } else {
        this.pauseAll()
      }
    })
  }

  // ----------------------------------------------------
  // Video Playback
  // ----------------------------------------------------

  handleVideoReady() {
    if (!this.isPostVisible) return

    requestAnimationFrame(() => {
      this.playCurrentSlide()
    })
  }

  playCurrentSlide(retriesLeft = 10) {
    if (!this.isPostVisible) return

    const slide = this.currentSlide()

    if (!slide) return

    const wrapper = slide.querySelector("[data-controller~='video-js']")

    if (!wrapper) {
      // Current slide is an image
      this.pauseAll()
      return
    }

    if (!wrapper.videoController) {
      if (retriesLeft > 0) {
        clearTimeout(this.readyRetryTimeout)

        this.readyRetryTimeout = setTimeout(() => {
          this.playCurrentSlide(retriesLeft - 1)
        }, 150)
      }

      return
    }

    this.videoWrappers().forEach(video => {
      if (video !== wrapper) {
        video.videoController?.pause()
      }
    })

    if (!wrapper.videoController.isPlaying()) {
      setTimeout(() => {
        wrapper.videoController?.play()
      }, 100)
    }
  }

  pauseAll() {
    this.videoWrappers().forEach(video => {
      video.videoController?.pause()
    })
  }

  videoWrappers() {
    return Array.from(
      this.element.querySelectorAll(
        "[data-controller~='video-js']"
      )
    )
  }

  handleTurboFrameLoad(event) {
    // Only react if this slider is inside the loaded frame
    if (!event.target.contains(this.element)) return

    requestAnimationFrame(() => {

      const $slider = $(this.sliderTarget)

      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('setPosition')
      } else {
        this.initializeSlider()
      }

      if (this.isPostVisible) {
        this.playCurrentSlide()
      }
    })
  }

  // ----------------------------------------------------
  // Slick Helpers
  // ----------------------------------------------------

  currentSlide() {
    if (!this.hasSliderTarget) return null

    if (this.sliderTarget.classList.contains("post-media-slider--single")) {
      return this.sliderTarget.querySelector(".post-media-item")
    }

    const $slider = $(this.sliderTarget)

    if (!$slider.hasClass("slick-initialized")) return null

    const slick = $slider.slick("getSlick")

    if (!slick) return null

    const slide =
      slick.$slides[slick.currentSlide]

    return slide || null
  }

  currentIndex() {
    if (!this.hasSliderTarget) return 0

    const $slider = $(this.sliderTarget)

    if (!$slider.hasClass("slick-initialized")) return 0

    return $slider.slick("slickCurrentSlide")
  }

  slideCount() {
    if (!this.hasSliderTarget) return 0

    const $slider = $(this.sliderTarget)

    if (!$slider.hasClass("slick-initialized")) return 0

    return $slider.slick("getSlick").slideCount
  }

  // ----------------------------------------------------
  // Navigation Helpers
  // ----------------------------------------------------

  next() {
    if (!this.hasSliderTarget) return

    $(this.sliderTarget).slick("slickNext")
  }

  prev() {
    if (!this.hasSliderTarget) return

    $(this.sliderTarget).slick("slickPrev")
  }

  goTo(index) {
    if (!this.hasSliderTarget) return

    $(this.sliderTarget).slick("slickGoTo", index)
  }

  // ----------------------------------------------------
  // Browser Visibility
  // ----------------------------------------------------

  handleTabVisibility() {
    if (document.hidden) {
      this.pauseAll()
      return
    }

    if (!this.isPostVisible) return

    requestAnimationFrame(() => {
      this.playCurrentSlide()
    })
  }

  // ----------------------------------------------------
  // Public API
  // ----------------------------------------------------

  refresh() {
    if (!this.hasSliderTarget) return

    const $slider = $(this.sliderTarget)

    if ($slider.hasClass("slick-initialized")) {
      $slider.slick("setPosition")

      if (this.isPostVisible) {
        requestAnimationFrame(() => {
          this.playCurrentSlide()
        })
      }
    }
  }

  // ----------------------------------------------------
  // Cleanup
  // ----------------------------------------------------

  teardown() {
    clearTimeout(this.readyRetryTimeout)
    clearTimeout(this.resizeTimeout)

    this.pauseAll()

    this.destroySlider()

    this.postObserver?.disconnect()
    this.postObserver = null
  }
}
